interface FilterOption {
  label: string;
  value: string;
}

interface FilterBarProps {
  types: FilterOption[];
  genres: FilterOption[];
  regions: FilterOption[];
  years: FilterOption[];
  activeType: string;
  activeGenre: string;
  activeRegion: string;
  activeYear: string;
  sort: string;
  keyword: string;
  expanded: boolean;
  onToggle: () => void;
  onChange: (key: string, value: string) => void;
  onReset: () => void;
}

function Row({
  label,
  options,
  active,
  onPick,
}: {
  label: string;
  options: FilterOption[];
  active: string;
  onPick: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2 border-b border-background-200 py-3 last:border-b-0 md:flex-row md:items-start md:gap-4">
      <span className="shrink-0 pt-1.5 text-xs font-semibold text-foreground-500 md:w-16 md:text-sm">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onPick(opt.value)}
            className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors md:text-sm ${
              active === opt.value
                ? 'bg-primary-500 text-foreground-950'
                : 'bg-secondary-200 text-secondary-950 hover:bg-secondary-300'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FilterBar({
  types,
  genres,
  regions,
  years,
  activeType,
  activeGenre,
  activeRegion,
  activeYear,
  sort,
  keyword,
  expanded,
  onToggle,
  onChange,
  onReset,
}: FilterBarProps) {
  const sortOptions: FilterOption[] = [
    { label: '最新', value: 'latest' },
    { label: '热门', value: 'popular' },
  ];

  const hasActiveFilter = activeType || activeGenre || activeRegion || activeYear || keyword;

  return (
    <div className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {keyword && (
            <span className="flex items-center gap-1.5 rounded-full bg-accent-500/15 px-3 py-1.5 text-xs text-accent-400">
              <i className="ri-search-line"></i>
              关键词：{keyword}
            </span>
          )}
          <span className="text-xs text-foreground-500 md:text-sm">共找到相关作品，可按条件筛选</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 rounded-md border border-background-300 p-1">
            {sortOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange('sort', opt.value)}
                className={`whitespace-nowrap rounded px-3 py-1.5 text-xs transition-colors ${
                  sort === opt.value ? 'bg-accent-500 text-background-50' : 'text-foreground-700 hover:text-foreground-950'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={onToggle}
            className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-xs text-foreground-800 md:hidden"
          >
            <i className="ri-filter-3-line"></i>
            筛选
          </button>
        </div>
      </div>

      <div className={`mt-3 ${expanded ? 'block' : 'hidden'} md:block`}>
        <Row label="类型" options={types} active={activeType} onPick={(v) => onChange('category', v)} />
        <Row label="题材" options={genres} active={activeGenre} onPick={(v) => onChange('genre', v)} />
        <Row label="地区" options={regions} active={activeRegion} onPick={(v) => onChange('region', v)} />
        <Row label="年代" options={years} active={activeYear} onPick={(v) => onChange('year', v)} />
      </div>

      {hasActiveFilter && (
        <button
          type="button"
          onClick={onReset}
          className="mt-3 inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-500 hover:text-foreground-950"
        >
          <i className="ri-refresh-line"></i>
          清空筛选
        </button>
      )}
    </div>
  );
}